import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  X, 
  FileText, 
  FileImage, 
  FileSpreadsheet,
  File,
  FileCode,
  FileArchive,
  Eye,
  Download,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getAuthHeaders } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// File type detection
const getFileType = (filename) => {
  if (!filename) return 'unknown';
  const ext = filename.split('.').pop()?.toLowerCase();
  
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
  const docExts = ['doc', 'docx', 'odt', 'rtf'];
  const spreadsheetExts = ['xls', 'xlsx', 'csv', 'ods'];
  const pdfExts = ['pdf'];
  const codeExts = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  const textExts = ['txt', 'md', 'log'];
  
  if (imageExts.includes(ext)) return 'image';
  if (docExts.includes(ext)) return 'document';
  if (spreadsheetExts.includes(ext)) return 'spreadsheet';
  if (pdfExts.includes(ext)) return 'pdf';
  if (codeExts.includes(ext)) return 'code';
  if (archiveExts.includes(ext)) return 'archive';
  if (textExts.includes(ext)) return 'text';
  
  return 'unknown';
};

// Get icon for file type
const FileIcon = ({ type, className }) => {
  const iconProps = { className: cn("w-5 h-5", className) };
  
  switch (type) {
    case 'image':
      return <FileImage {...iconProps} className={cn(iconProps.className, "text-green-500")} />;
    case 'document':
      return <FileText {...iconProps} className={cn(iconProps.className, "text-blue-500")} />;
    case 'spreadsheet':
      return <FileSpreadsheet {...iconProps} className={cn(iconProps.className, "text-emerald-500")} />;
    case 'pdf':
      return <FileText {...iconProps} className={cn(iconProps.className, "text-red-500")} />;
    case 'code':
      return <FileCode {...iconProps} className={cn(iconProps.className, "text-purple-500")} />;
    case 'archive':
      return <FileArchive {...iconProps} className={cn(iconProps.className, "text-amber-500")} />;
    case 'text':
      return <FileText {...iconProps} className={cn(iconProps.className, "text-gray-500")} />;
    default:
      return <File {...iconProps} className={cn(iconProps.className, "text-gray-400")} />;
  }
};

// Get background color for file type badge
const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'image': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'document': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'spreadsheet': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    case 'pdf': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'code': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'archive': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
};

// Attachment Preview Modal
const AttachmentPreviewModal = ({ attachment, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  
  const fileType = getFileType(attachment?.original_filename);
  const isImage = fileType === 'image';
  
  React.useEffect(() => {
    if (open && isImage && attachment?.attachment_id) {
      setLoading(true);
      setError(null);
      
      // Create object URL for image preview
      fetch(`${API}/attachments/${attachment.attachment_id}`, {
        headers: getAuthHeaders()
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to load image');
          return res.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setImageUrl(url);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
      
      return () => {
        if (imageUrl) URL.revokeObjectURL(imageUrl);
      };
    }
  }, [open, isImage, attachment?.attachment_id]);
  
  const handleDownload = async () => {
    try {
      const response = await fetch(`${API}/attachments/${attachment.attachment_id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  
  if (!attachment) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileIcon type={fileType} />
            <span className="truncate">{attachment.original_filename}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* File Info */}
          <div className="flex flex-wrap gap-2 text-sm">
            <span className={cn("px-2 py-1 rounded-full text-xs font-medium", getTypeBadgeColor(fileType))}>
              {fileType.toUpperCase()}
            </span>
            <span className="text-muted-foreground">
              {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
            </span>
            {attachment.mime_type && (
              <span className="text-muted-foreground">
                {attachment.mime_type}
              </span>
            )}
          </div>
          
          {/* Preview Area */}
          <div className="min-h-[200px] max-h-[400px] overflow-auto border rounded-lg bg-muted/30 flex items-center justify-center">
            {isImage ? (
              loading ? (
                <div className="flex flex-col items-center gap-2 p-8">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading preview...</span>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center gap-2 p-8 text-destructive">
                  <X className="w-8 h-8" />
                  <span className="text-sm">{error}</span>
                </div>
              ) : (
                <img 
                  src={imageUrl} 
                  alt={attachment.original_filename}
                  className="max-w-full max-h-[380px] object-contain"
                />
              )
            ) : (
              <div className="flex flex-col items-center gap-4 p-8">
                <FileIcon type={fileType} className="w-16 h-16" />
                <div className="text-center">
                  <p className="font-medium">{attachment.original_filename}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Preview not available for this file type
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Attachment Item Component
export const AttachmentItem = ({ 
  attachment, 
  onRemove, 
  showPreview = true,
  showSize = true,
  compact = false
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileType = getFileType(attachment?.original_filename);
  const isPreviewable = fileType === 'image';
  
  if (!attachment) return null;
  
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md text-sm group">
          <FileIcon type={fileType} className="w-4 h-4 flex-shrink-0" />
          <span className="truncate max-w-[150px]" title={attachment.original_filename}>
            {attachment.original_filename}
          </span>
          {showPreview && isPreviewable && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="w-3 h-3" />
            </Button>
          )}
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
              onClick={() => onRemove(attachment.attachment_id)}
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
        
        {showPreview && (
          <AttachmentPreviewModal
            attachment={attachment}
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </>
    );
  }
  
  return (
    <>
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/50 group hover:bg-muted transition-colors">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Thumbnail or Icon */}
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            getTypeBadgeColor(fileType).replace('text-', 'bg-').split(' ')[0] + '/20'
          )}>
            <FileIcon type={fileType} className="w-5 h-5" />
          </div>
          
          {/* File Info */}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm truncate" title={attachment.original_filename}>
              {attachment.original_filename}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", getTypeBadgeColor(fileType))}>
                {fileType.toUpperCase()}
              </span>
              {showSize && attachment.file_size && (
                <span>{(attachment.file_size / 1024).toFixed(1)} KB</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 ml-2">
          {showPreview && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPreviewOpen(true)}
              title={isPreviewable ? "Preview" : "View details"}
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onRemove(attachment.attachment_id)}
              title="Remove"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {showPreview && (
        <AttachmentPreviewModal
          attachment={attachment}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
};

// Attachment List Component
export const AttachmentList = ({ 
  attachments = [], 
  onRemove, 
  showPreview = true,
  compact = false,
  emptyMessage = "No attachments"
}) => {
  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        {emptyMessage}
      </div>
    );
  }
  
  return (
    <div className={cn("space-y-2", compact && "flex flex-wrap gap-2 space-y-0")}>
      {attachments.map(att => (
        <AttachmentItem
          key={att.attachment_id}
          attachment={att}
          onRemove={onRemove}
          showPreview={showPreview}
          compact={compact}
        />
      ))}
    </div>
  );
};

// Export helper functions
export { getFileType, FileIcon, getTypeBadgeColor };

export default AttachmentList;
